import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';

const welcomeMessages = {
  ar: `مرحباً بك في جودورا – مساعدك القانوني الموثوق\n\nأستطيع مساعدتك بمعلومات قانونية عامة في دولة الإمارات\n\nيمكنك التحدث معي باللغة العربية، الإنجليزية، الأردية، أو الصينية.\n\nملاحظة: أنا مساعد ذكي ولست محامياً مرخصاً. في المسائل المعقدة أو العاجلة، أنصحك بالتواصل مع محامٍ مختص.\n\nكيف يمكنني مساعدتك اليوم؟`,
  en: `Welcome to Judaura – your trusted legal assistant\n\nI can help you with general legal information related to the United Arab Emirates.\n\nYou may speak with me in Arabic, English, Urdu, or Chinese.\n\nNote: I am an intelligent assistant, not a licensed lawyer. For complex or urgent matters, I recommend consulting a qualified lawyer.\n\nHow can I assist you today?`,
  zh: `欢迎使用 Judaura – 您值得信赖的法律助手\n\n我可以为您提供与阿联酋相关的一般法律信息。\n\n您可以使用 阿拉伯语、英语、乌尔都语 或 中文 与我交流。\n\n注意：我是一位智能助手，并非持牌律师。如遇复杂或紧急情况，建议您咨询专业律师。\n\n我今天可以为您提供哪些帮助？`,
  ur: `جودورا – آپ کا قابلِ اعتماد قانونی معاون میں خوش آمدید\n\nمیں آپ کو متحدہ عرب امارات سے متعلق عمومی قانونی معلومات فراہم کر سکتا ہوں۔\n\nآپ مجھ سے عربی، انگریزی، اردو، یا چینی زبان میں بات کر سکتے ہیں۔\n\nنوٹ: میں ایک ذہین معاون ہوں، лиценس یافتہ وکیل نہیں۔ پیچیدہ یا فوری معاملات کے لیے کسی مستند وکیل سے رجوع کریں۔\n\nمیں آج آپ کی کس حوالے سے مدد کر سکتا ہوں؟`,
};

const App = () => {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [language, setLanguage] = useState('en');
  const [isLoading, setIsLoading] = useState(false);
  const [file, setFile] = useState(null);
  const [uploadStatus, setUploadStatus] = useState('');
  const [temperature] = useState(0.5);
  const [maxTokens] = useState(1000);
  const [systemPrompt] = useState(`
You are a legal assistant named Judaura, specialized in UAE laws and regulations across all emirates (Abu Dhabi, Dubai, Sharjah, Ajman, Umm Al Quwain, Ras Al Khaimah, Fujairah). Provide clear, professional, and concise non-binding guidance on legal documents, court procedures, civil, corporate, and arbitration matters, ensuring answers apply to the entire UAE unless a specific emirate is mentioned. If a document is provided, prioritize its content for answers related to UAE laws and regulations, extracting relevant information to address the user’s query. For Arabic queries, use a professional Emirati dialect (e.g., "مرحبا", "لو سمحت", "بالتوفيق"). Respond in the same language as the user (Arabic, English, Chinese, or Urdu), maintaining a respectful tone suitable for legal advice.

For any query where the user needs lawyer advice or a lawyer (e.g., complex, specific, or actionable legal matters, or when analyzing uploaded documents), always recommend consulting a licensed UAE lawyer and explicitly offer Judaura’s legal services with contact details. Include this recommendation in every response requiring legal expertise, even if the answer is clear, using: 
Arabic: "للحصول على استشارة قانونية دقيقة، يُفضل التواصل مع محامٍ مرخّص. فريقنا القانوني في جودورا جاهز لمساعدتك، تواصل عبر Ameera@Judaura.com أو زور Judaura.com."
English: "For precise legal advice, it’s best to consult a licensed lawyer. Our legal team at Judaura is ready to assist; contact us at Ameera@Judaura.com or visit Judaura.com."
Chinese: "为了获得准确的法律建议，建议咨询持牌律师。Judaura 的法律团队随时为您提供帮助，请联系 Ameera@Judaura.com 或访问 Judaura.com。"
Urdu: "درست قانونی مشورے کے لیے، مستند وکیل سے رجوع کرنا بہتر ہے۔ جودورا کی قانونی ٹیم آپ کی مدد کے لیے تیار ہے؛ Ameera@Judaura.com پر رابطہ کریں یا Judaura.com پر جائیں۔"

If you are uncertain, lack context, cannot extract relevant information from the document, or cannot provide a legally accurate response, use the following fallback responses in the user’s language: 
Arabic: "مع الأسف، ما عندي معلومات كافية للإجابة بدقة، أو الوثيقة لا تحتوي على المعلومات المطلوبة. يُفضل التواصل مع محامٍ مرخّص، أو مع فريقنا القانوني عبر Ameera@Judaura.com أو زيارة Judaura.com للتحدث عن خدماتنا القانونية."
English: "Sorry, I don’t have enough information to answer accurately, or the document doesn’t contain the required details. It’s best to speak with a licensed lawyer, or contact our legal team at Ameera@Judaura.com or visit Judaura.com to explore our services."
Chinese: "抱歉，我没有足够的信息来准确回答，或者文档中没有所需细节。建议您咨询持牌律师，或联系 Judaura 法律团队（邮箱：Ameera@Judaura.com），或访问 Judaura.com 了解我们的服务。"
Urdu: "معذرت، میرے پاس درست جواب دینے کے لیے کافی معلومات نہیں ہیں، یا دستاویز میں مطلوبہ تفصیلات نہیں ہیں۔ بہتر ہوگا کہ آپ کسی مستند وکیل سے رجوع کریں یا ہماری قانونی ٹیم سے Ameera@Judaura.com پر رابطہ کریں، یا Judaura.com پر ہماری خدمات کے بارے میں جانیں۔"

Use markdown for quotes (e.g., "quote") and emphasis (e.g., **bold**, *italic*). Ensure responses are complete, formatted with line breaks for readability, and maintain context from previous messages and uploaded documents.
  `);
  const [presencePenalty] = useState(0.2);
  const [tokenUsage, setTokenUsage] = useState(0);
  const chatContainerRef = useRef(null);

  useEffect(() => {
    setMessages([{ role: 'assistant', content: welcomeMessages[language] }]);
  }, [language]);

  useEffect(() => {
    if (chatContainerRef.current) {
      chatContainerRef.current.scrollTo({
        top: chatContainerRef.current.scrollHeight,
        behavior: 'smooth',
      });
    }
  }, [messages, isLoading]);

  const parseMarkdown = (text) => {
    let formattedText = text
      .replace(/"([^"]+)"/g, '<blockquote class="border-l-4 border-teal-500 pl-4 italic my-2 text-gray-300">$1</blockquote>')
      .replace(/\*\*([^*]+)\*\*/g, '<strong class="text-white">$1</strong>') // Fixed regex
      .replace(/\*([^*]+)\*/g, '<em class="text-gray-300">$1</em>') // Fixed regex
      .replace(/\n/g, '<br/>');
    return { __html: formattedText };
  };

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile && ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document', 'text/plain'].includes(selectedFile.type)) {
      setFile(selectedFile);
      setUploadStatus(language === 'ar' ? 'تم اختيار الملف' : language === 'en' ? 'File selected' : language === 'zh' ? '文件已选择' : 'فائل منتخب ہوگئی');
    } else {
      setFile(null);
      setUploadStatus(language === 'ar' ? 'يرجى اختيار ملف بصيغة PDF أو Word أو TXT' : language === 'en' ? 'Please select a PDF, Word, or TXT file' : language === 'zh' ? '请选择 PDF、Word 或 TXT 文件' : 'براہ کرم PDF، Word یا TXT فائل منتخب کریں');
    }
  };

  const sendMessage = async () => {
    if (!input.trim() && !file) return;

    const userMessage = { role: 'user', content: input || 'Uploaded a document' };
    setMessages((prev) => [...prev, userMessage]);
    setInput('');
    setIsLoading(true);

    try {
      const formData = new FormData();
      const conversation = [
        { role: 'system', content: systemPrompt },
        ...messages
          .filter((msg) => msg.role !== 'assistant' || !msg.content.startsWith(welcomeMessages[language]))
          .map((msg) => ({ role: msg.role, content: msg.content })),
        { role: 'user', content: input || 'Please analyze the uploaded document for UAE law-related information.' },
      ];
      formData.append('conversation', JSON.stringify(conversation));
      formData.append('temperature', temperature);
      formData.append('max_tokens', maxTokens);
      formData.append('system_prompt', systemPrompt);
      formData.append('presence_penalty', presencePenalty);
      if (file) {
        formData.append('file', file);
      }

      const response = await axios.post('https://j-cbotbackend.vercel.app/chat', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      setIsLoading(false);
      setFile(null);
      setUploadStatus('');
      const assistantMessage = { role: 'assistant', content: response.data.reply };
      setMessages((prev) => [...prev, assistantMessage]);
      setTokenUsage((prev) => prev + response.data.tokens_used);
    } catch (error) {
      setIsLoading(false);
      setFile(null);
      setUploadStatus('');
      console.error('Error details:', error.response || error.message);
      const errorMessage = {
        role: 'assistant',
        content: `Error: ${error.response?.data?.error || error.message}. Please try again or contact Ameera@Judaura.com.`,
      };
      setMessages((prev) => [...prev, errorMessage]);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-black to-gray-900 flex flex-col font-sans">
      <div className="w-full max-w-4xl mx-auto flex justify-end p-4">
        <div className="relative inline-block">
          <select
            value={language}
            onChange={(e) => setLanguage(e.target.value)}
            className="appearance-none bg-gray-800 text-gray-300 border border-teal-500/30 rounded-full px-4 py-2 pr-8 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
          >
            <option value="ar">العربية</option>
            <option value="en">English</option>
            <option value="zh">中文</option>
            <option value="ur">اردو</option>
          </select>
          <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-300">
            <svg className="fill-current h-4 w-4" viewBox="0 0 20 20"><path d="M10 12l-6-6h12l-6 6z"/></svg>
          </div>
        </div>
      </div>
      <div className="w-full max-w-4xl mx-auto p-4">
        <h1 className="text-4xl font-bold text-white text-center mb-6">Judaura Legal Assistant</h1>
        <div className="mb-4">
          <p className="text-sm text-gray-400">Total Tokens Used: {tokenUsage}</p>
        </div>
        <div
          ref={chatContainerRef}
          className="h-[70vh] bg-gray-900 rounded-xl shadow-lg p-4 overflow-y-auto border border-teal-500/20 scroll-smooth"
        >
          <div className="space-y-4">
            {messages.map((msg, index) => (
              <div
                key={index}
                className={`p-4 rounded-lg shadow-md transition-all max-w-[80%] ${
                  msg.role === 'user' ? 'bg-teal-500/20 ml-auto text-right text-white' : 'bg-gray-800 mr-auto text-left text-gray-200'
                }`}
              >
                <span className="font-semibold text-teal-400">{msg.role === 'user' ? 'You' : 'Judaura'}:</span>
                <span
                  className="block leading-relaxed text-sm"
                  dangerouslySetInnerHTML={parseMarkdown(msg.content)}
                />
              </div>
            ))}
            {isLoading && (
              <div className="p-4 rounded-lg bg-gray-800 max-w-[80%] mr-auto text-left shadow-md">
                <span className="font-semibold text-teal-400">Judaura:</span>
                <span className="block text-gray-400 animate-pulse flex items-center space-x-1 text-sm">
                  <span>{language === 'ar' ? 'جاري التفكير' : language === 'en' ? 'Thinking' : language === 'zh' ? '正在思考' : 'سوچ رہا ہوں'}</span>
                  <span className="w-1 h-1 bg-teal-500 rounded-full animate-bounce"></span>
                  <span className="w-1 h-1 bg-teal-500 rounded-full animate-bounce delay-100"></span>
                  <span className="w-1 h-1 bg-teal-500 rounded-full animate-bounce delay-200"></span>
                </span>
              </div>
            )}
            <div className="h-1" />
          </div>
        </div>
      </div>
      <div className="w-full max-w-4xl mx-auto p-4 bg-gray-900 shadow-lg">
        <div className="flex items-center space-x-2">
          <label className="flex items-center cursor-pointer">
            <input
              type="file"
              accept=".pdf,.doc,.docx,.txt"
              onChange={handleFileChange}
              className="hidden"
            />
            <span className="p-3 bg-gray-800 text-teal-400 rounded-xl hover:bg-gray-700 transition-colors text-sm font-medium">
              {language === 'ar' ? 'رفع ملف' : language === 'en' ? 'Upload File' : language === 'zh' ? '上传文件' : 'فائل اپ لوڈ کریں'}
            </span>
          </label>
          {uploadStatus && (
            <span className="text-sm text-gray-400">{uploadStatus}</span>
          )}
          <input
            type="text"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && sendMessage()}
            className="flex-1 p-3 bg-gray-800 text-gray-200 border border-teal-500/30 rounded-l-xl focus:outline-none focus:ring-2 focus:ring-teal-500 text-sm"
            placeholder={
              language === 'ar' ? 'اسأل عن قوانين وأنظمة الإمارات...' :
              language === 'en' ? 'Ask about UAE laws and regulations...' :
              language === 'zh' ? '询问有关阿联酋法律和法规的问题...' :
              'متحدہ عرب امارات کے قوانین اور ضوابط کے بارے میں پوچھیں...'
            }
          />
          <button
            onClick={sendMessage}
            className="p-3 bg-teal-500 text-white rounded-r-xl hover:bg-teal-600 transition-colors text-sm font-medium"
          >
            {language === 'ar' ? 'إرسال' : language === 'zh' ? '发送' : language === 'ur' ? 'بھیجیں' : 'Send'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default App;